/**
 * Upload file to S3 using multipart upload for large files (>= MULTIPART_UPLOAD_MIN_SIZE)
 *
 * Backend provides necessary S3 configuration via FileUploadIntermediateDto:
 * - For files < MULTIPART_UPLOAD_MIN_SIZE: Only `url` (presigned URL) is provided and used for simple PUT upload
 * - For files >= MULTIPART_UPLOAD_MIN_SIZE: `s3Bucket`, `s3Key`, and `temporaryS3Config` are provided for multipart upload
 *
 * The backend returns temporary S3 credentials (STS tokens) with minimal permissions
 * scoped to the specific upload path for security.
 */

import {
    S3Client,
    CreateMultipartUploadCommand,
    UploadPartCommand,
    CompleteMultipartUploadCommand,
    AbortMultipartUploadCommand,
} from '@aws-sdk/client-s3';
import { MULTIPART_UPLOAD_MIN_SIZE, MAX_CONCURRENT_PART_UPLOADS } from '../constants';
import { logAttachmentsError } from '../helpers';

/**
 * Upload file to S3 using multipart upload with progress tracking for large files,
 * or simple PUT for small files
 */
export async function uploadToS3({
    presignedUrl,
    bucket,
    key,
    s3Config,
    file,
    expectedContentType,
    onProgress,
}: {
    presignedUrl: string;
    bucket?: string;
    key?: string;
    s3Config?: {
        region: string;
        accessKeyId: string;
        secretAccessKey: string;
        sessionToken: string;
    };
    file: File;
    expectedContentType?: string;
    onProgress: (uploadedBytes: number) => void;
}): Promise<void> {
    const PART_SIZE = MULTIPART_UPLOAD_MIN_SIZE;
    // For small files, use simple PUT to presigned URL (faster, less overhead)
    if (file.size < PART_SIZE) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    onProgress(event.loaded);
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    onProgress(file.size);
                    resolve();
                } else {
                    reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
                }
            });

            xhr.addEventListener('error', () => {
                reject(new Error('Network error during upload'));
            });

            xhr.open('PUT', presignedUrl);

            // Set required headers based on presigned URL signature
            const urlObj = new URL(presignedUrl);
            const signedHeaders = urlObj.searchParams.get('X-Amz-SignedHeaders');

            if (signedHeaders?.includes('content-type')) {
                if (!expectedContentType) {
                    // attempting to upload without knowing the content-type will most likely fail with a 403
                    // and even if it were to succeed - the uploaded file may be in a malformed state
                    throw new Error('No content type returned from server');
                }

                xhr.setRequestHeader('Content-Type', expectedContentType);
            }

            if (signedHeaders?.includes('x-amz-server-side-encryption')) {
                xhr.setRequestHeader('x-amz-server-side-encryption', 'AES256');
            }

            xhr.send(file);
        });
    }

    // For larger files, use AWS SDK multipart upload
    // Backend provides s3Config (temporary credentials)
    if (!s3Config || !bucket || !key) {
        throw new Error(
            'Multipart upload requires S3 credentials from backend. The file will be uploaded by aws-sdk multipart upload but temporaryS3Config was not provided in the FileUploadIntermediateDto response.'
        );
    }

    // Create S3 client with credentials from backend
    const s3Client = new S3Client({
        region: s3Config.region,
        credentials: {
            accessKeyId: s3Config.accessKeyId,
            secretAccessKey: s3Config.secretAccessKey,
            sessionToken: s3Config.sessionToken,
        },
    });

    const totalParts = Math.ceil(file.size / PART_SIZE);

    // Create multipart upload
    const createCommand = new CreateMultipartUploadCommand({
        Bucket: bucket,
        Key: key,
        ContentType: file.type,
    });

    const { UploadId } = await s3Client.send(createCommand);

    if (!UploadId) {
        throw new Error('Failed to create multipart upload');
    }

    try {
        // Track progress using an array to avoid race conditions from parallel part uploads
        const partSizes: number[] = [];
        const completedPartFlags: boolean[] = new Array(totalParts).fill(false);
        const completedParts: { ETag: string; PartNumber: number }[] = [];

        // Helper function to upload a single part
        const uploadPart = async (
            partNumber: number
        ): Promise<{ ETag: string; PartNumber: number }> => {
            const start = (partNumber - 1) * PART_SIZE;
            const end = Math.min(start + PART_SIZE, file.size);
            const partData = file.slice(start, end);
            const partIndex = partNumber - 1;

            const arrayBuffer = await partData.arrayBuffer();
            const uploadCommand = new UploadPartCommand({
                Bucket: bucket,
                Key: key,
                PartNumber: partNumber,
                UploadId,
                Body: new Uint8Array(arrayBuffer),
            });

            const result = await s3Client.send(uploadCommand);

            // Mark part as completed and calculate total uploaded bytes
            completedPartFlags[partIndex] = true;
            const uploadedBytes = completedPartFlags.reduce(
                (total: number, completed: boolean, index: number) =>
                    total + (completed ? partSizes[index] : 0),
                0
            );
            onProgress(uploadedBytes);

            return {
                ETag: result.ETag!,
                PartNumber: partNumber,
            };
        };

        // Pre-calculate all part sizes for accurate progress tracking
        for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
            const start = (partNumber - 1) * PART_SIZE;
            const end = Math.min(start + PART_SIZE, file.size);
            partSizes[partNumber - 1] = end - start;
        }

        // Upload parts in batches to control concurrency
        for (let i = 0; i < totalParts; i += MAX_CONCURRENT_PART_UPLOADS) {
            const batchSize = Math.min(MAX_CONCURRENT_PART_UPLOADS, totalParts - i);
            const batchPromises = Array.from({ length: batchSize }, (_, j) =>
                uploadPart(i + j + 1)
            );

            const batchResults = await Promise.all(batchPromises);
            completedParts.push(...batchResults);
        }

        // Complete multipart upload
        const completeCommand = new CompleteMultipartUploadCommand({
            Bucket: bucket,
            Key: key,
            UploadId,
            MultipartUpload: {
                Parts: completedParts.sort((a, b) => a.PartNumber - b.PartNumber),
            },
        });

        await s3Client.send(completeCommand);
    } catch (error) {
        // Abort multipart upload on error
        try {
            const abortCommand = new AbortMultipartUploadCommand({
                Bucket: bucket,
                Key: key,
                UploadId,
            });
            await s3Client.send(abortCommand);
        } catch (abortError) {
            // Log abort failure but don't throw - we want to throw the original error
            logAttachmentsError(abortError as Error);
        }

        throw error;
    } finally {
        // Clear sensitive data from memory
        if (s3Config) {
            s3Config.accessKeyId = '';
            s3Config.secretAccessKey = '';
            s3Config.sessionToken = '';
        }
        if (s3Client) {
            s3Client.destroy();
        }
    }
}
